import { css } from '@emotion/react';
import { Autocomplete, FormControlLabel, IconButton, MenuItem, Switch, TextField, Tooltip } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

import { DashboardSortKey, DashboardSortOrder } from 'store/fetchDashboardRuns';

import IProject from 'types/IProject';
import {
  ACTIVITY_WINDOWS,
  ActivityWindow,
  aggregateRunStates,
  isAggregateRunState,
  isTaskStateFilter,
  taskStateFilters,
} from 'types/RunState';

export const SORT_OPTIONS: { key: DashboardSortKey; label: string }[] = [
  { key: 'updated_on', label: 'Last updated' },
  { key: 'created_on', label: 'Created' },
  { key: 'name', label: 'Name' },
  { key: 'running_tasks', label: 'Running tasks' },
];

export const STATE_FILTERS = ['', ...aggregateRunStates] as const;
export type StateFilter = (typeof STATE_FILTERS)[number];

export const TASK_STATE_FILTERS = ['', ...taskStateFilters] as const;
export type TaskStateFilterValue = (typeof TASK_STATE_FILTERS)[number];

export function nextStateFilter(current: StateFilter, value: string): StateFilter | null {
  if (value === current) return '';
  if (!isAggregateRunState(value)) return null;
  return value;
}

export function nextTaskStateFilter(current: TaskStateFilterValue, value: string): TaskStateFilterValue | null {
  if (value === current) return '';
  if (!isTaskStateFilter(value)) return null;
  return value;
}

export type FilterUser = {
  id: string;
  username: string;
  full_name?: string;
  email?: string;
};

const emptyUsers: FilterUser[] = [];

function userLabel(user: FilterUser): string {
  return user.full_name?.trim() || user.email || user.username;
}

interface FilterBarProps {
  searchId: string;
  search: string;
  onSearchChange: (value: string) => void;
  state: StateFilter;
  onStateChange: (value: StateFilter) => void;
  taskState: TaskStateFilterValue;
  onTaskStateChange: (value: TaskStateFilterValue) => void;
  activityWindow: ActivityWindow;
  onActivityWindowChange: (value: ActivityWindow) => void;
  users?: FilterUser[];
  userId: string;
  onUserIdChange: (id: string) => void;
  hasRunningTasks: boolean;
  onHasRunningTasksChange: (value: boolean) => void;
  sort: DashboardSortKey;
  onSortChange: (key: DashboardSortKey) => void;
  order: DashboardSortOrder;
  onOrderChange: (value: DashboardSortOrder) => void;
  projects?: {
    options: IProject[];
    selected: IProject[];
    onChange: (projects: IProject[]) => void;
  };
}

export default function FilterBar({ users = emptyUsers, ...props }: FilterBarProps) {
  const userInList = users.some((user) => user.id === props.userId);
  return (
    <div
      css={css`
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 16px;

        > *:first-of-type {
          flex: 1 1 240px;
        }

        .MuiOutlinedInput-root {
          height: 36px;
        }

        .MuiAutocomplete-root .MuiOutlinedInput-root {
          height: auto;
          min-height: 36px;
        }
      `}
    >
      <TextField
        id={props.searchId}
        label="Search"
        placeholder="Name or ID"
        variant="outlined"
        size="small"
        value={props.search}
        onChange={(e) => props.onSearchChange(e.target.value)}
      />
      {props.projects && (
        <Autocomplete
          multiple
          size="small"
          options={props.projects.options}
          getOptionLabel={(project) => project.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={props.projects.selected}
          onChange={(_, value) => props.projects?.onChange(value)}
          renderInput={(params) => <TextField {...params} label="Projects" size="small" />}
          css={css`
            min-width: 240px;
          `}
        />
      )}
      <TextField
        id={`${props.searchId}-state`}
        label="State"
        select
        size="small"
        value={props.state}
        onChange={(e) => {
          const next = e.target.value as StateFilter;
          if (next === props.state) return;
          props.onStateChange(next);
        }}
        css={css`
          min-width: 180px;
        `}
      >
        <MenuItem value="">All states</MenuItem>
        {aggregateRunStates.map((runState) => (
          <MenuItem key={runState} value={runState}>
            {runState}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        id={`${props.searchId}-activity-window`}
        label="Active"
        select
        size="small"
        value={props.activityWindow}
        onChange={(e) => {
          const next = e.target.value as ActivityWindow;
          if (next === props.activityWindow) return;
          props.onActivityWindowChange(next);
        }}
        css={css`
          min-width: 150px;
        `}
      >
        {ACTIVITY_WINDOWS.map((window) => (
          <MenuItem key={window || 'any'} value={window}>
            {window ? `Last ${window} days` : 'Any time'}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        id={`${props.searchId}-task-state`}
        label="Has task in state"
        select
        size="small"
        value={props.taskState}
        onChange={(e) => {
          const next = e.target.value as TaskStateFilterValue;
          if (next === props.taskState) return;
          props.onTaskStateChange(next);
        }}
        css={css`
          min-width: 180px;
        `}
      >
        <MenuItem value="">Any task state</MenuItem>
        {taskStateFilters.map((taskState) => (
          <MenuItem key={taskState} value={taskState}>
            {taskState}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        id={`${props.searchId}-user`}
        label="User"
        select
        size="small"
        value={props.userId}
        onChange={(e) => {
          if (e.target.value === props.userId) return;
          props.onUserIdChange(e.target.value);
        }}
        css={css`
          min-width: 180px;
        `}
        slotProps={{
          select: {
            MenuProps: { slotProps: { paper: { style: { maxHeight: 320 } } } },
          },
        }}
      >
        <MenuItem value="">All users</MenuItem>
        {props.userId && !userInList && <MenuItem value={props.userId}>{props.userId}</MenuItem>}
        {users.map((user) => (
          <MenuItem key={user.id} value={user.id}>
            {userLabel(user)}
          </MenuItem>
        ))}
      </TextField>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={props.hasRunningTasks}
            onChange={(e) => props.onHasRunningTasksChange(e.target.checked)}
          />
        }
        label="Has running tasks"
      />
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 4px;
          flex: none;
        `}
      >
        <TextField
          id={`${props.searchId}-sort`}
          label="Sort by"
          select
          size="small"
          value={props.sort}
          onChange={(e) => {
            const next = e.target.value as DashboardSortKey;
            if (next === props.sort) return;
            props.onSortChange(next);
          }}
          css={css`
            min-width: 160px;
          `}
        >
          {SORT_OPTIONS.map((option) => (
            <MenuItem key={option.key} value={option.key}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Tooltip title={props.order === 'desc' ? 'Descending' : 'Ascending'}>
          <IconButton
            onClick={() => props.onOrderChange(props.order === 'desc' ? 'asc' : 'desc')}
            size="small"
            sx={{ flex: 'none' }}
          >
            {props.order === 'desc' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
}
